import AutoNumeric from 'autonumeric';
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartSettingsPopupModel } from "../models/chart-style/chart-settings-popup.model";
import { ChartStyleSettingsModel } from '../models/chart-style/chart-style-settings.model';
import { QuoteType } from '../models/trade/trade.model';
import { IComponent } from "./component.interface";
import { TradeOrderTypeComponent } from './menu/trade-order-type.component';
import { PnlComponent } from './pnl.component';
import { TradeComponent } from './trade.component';

export class ChatSettingsPopupComponent implements IComponent {
    public id = '';

    private element: HTMLElement;
    private popup: HTMLElement | null = null;
    private base: IChartContainer;

    private quoteQuantityInput: AutoNumeric | null = null;

    private inited = false;
    constructor(element: HTMLElement, base: IChartContainer) {
        this.element = element
        this.base = base;
        
        this.base.chartService.subscribeChartStyleData((style: ChartStyleSettingsModel) => {
            setTimeout(() => {
                if (this.inited) {
                    return;
                }

                this.inited = true;
                
                if (!style.chartSettingsPopupEnabled) {
                    return;
                }
                    
                this.create();
                this.onClick();
                this.onBodyClick();
            }, 200);
        });
    }

    private onClick() {
        this.element.addEventListener('click', () => {
            this.show();
        });

        this.popup?.querySelectorAll<HTMLElement>('.submit').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                let model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
                if (!model) {
                    model = new ChartSettingsPopupModel();
                }

                const checkboxDisplayConfirmPopup = this.popup?.querySelector<HTMLInputElement>('#chart-settings-dont-show-again');
                if (checkboxDisplayConfirmPopup) {
                    const shouldDisplayConfirmPopup = checkboxDisplayConfirmPopup.checked;
                    model.displayOrderConfirmationPopup = shouldDisplayConfirmPopup;
                }

                if (this.base.tiChartSeries.isPreMarketEnabled()) {
                    const checkboxShowPremarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-per-market');
                    const shouldShowPremarket = checkboxShowPremarket?.checked ?? false;
                    model.displayPremarket = shouldShowPremarket;
                }

                if (this.base.tiChartSeries.isPostMarketEnabled()) {
                    const checkboxShowPostmarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-post-market');
                    const shouldShowPostmarket = checkboxShowPostmarket?.checked ?? false;
                    model.displayPostmarket = shouldShowPostmarket;
                }
                
                const checkboxDisplayZoomAndScroll = this.popup?.querySelector<HTMLInputElement>('#chart-settings-zoom-and-scroll');
                if (checkboxDisplayZoomAndScroll) {
                    const shouldDisplayZoomAndScroll = checkboxDisplayZoomAndScroll.checked;
                    model.displayChartSettingZoomAndScroll = shouldDisplayZoomAndScroll;
                }

                const checkboxDisplayMainChartChangePercent = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-main-chart-change-percent');
                if (checkboxDisplayMainChartChangePercent) {
                    const shouldDisplayMainChartChangePercent = checkboxDisplayMainChartChangePercent.checked;
                    model.displayMainChartChangePercentVisibleCandles = shouldDisplayMainChartChangePercent;
                }

                const checkboxDisplayBuySellFlags = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-buy-sell-flags');
                if (checkboxDisplayBuySellFlags) {
                    const shouldDisplayBuySellFlags = checkboxDisplayBuySellFlags.checked;
                    model.displayChartSettingBuySellFlags = shouldDisplayBuySellFlags;
                }

                const checkboxPnPBrowserChartsByDefault = this.popup?.querySelector<HTMLInputElement>('#chart-settings-pnp-browser-charts-by-default');
                if (checkboxPnPBrowserChartsByDefault) {
                    const shouldDisplayPnPBrowserChartsByDefault = checkboxPnPBrowserChartsByDefault.checked;
                    model.pnpBrowserChartsByDefault = shouldDisplayPnPBrowserChartsByDefault;
                }

                const checkboxEnableRightEdgePadding = this.popup?.querySelector<HTMLInputElement>('#chart-settings-enable-right-edge-padding');
                if (checkboxEnableRightEdgePadding) {
                    const shouldDisplayRightEdgePadding = checkboxEnableRightEdgePadding.checked;
                    model.enableRightEdgePadding = shouldDisplayRightEdgePadding;
                }

                const defaultTradeSizeOption = this.popup?.querySelector<HTMLElement>('.default-trade-size li.selected');
                if (defaultTradeSizeOption) {
                    model.defaultQuoteType = defaultTradeSizeOption.getAttribute('data-value') as QuoteType;
                }

                if (this.quoteQuantityInput) {
                    model.defaultQuoteQuantity = this.quoteQuantityInput.getNumber();
                }

                this.base.chartService.requestSaveChartSettingsPopupModel(model);

                if (this.base.tiChartSeries?.chartStyleSettings) {
                    this.base.tiChartSeries.chartStyleSettings.chartSettingsPopupModel = model;
                    this.base.tiChartSeries.onRedraw();
                }

                const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
                const tradeComponent = this.base.getComponentsByType(TradeComponent)?.[0] as TradeComponent;
                const tradeOrderTypeComponents = this.base.getComponentsByType(TradeOrderTypeComponent) as Array<TradeOrderTypeComponent>;
                if (pnl) {
                    pnl.preSelectedQuoteType = model.defaultQuoteType;
                    pnl.preSelectedShares = model.defaultQuoteQuantity;
                }

                if (tradeComponent && pnl && !pnl.selectedAccount) {
                    tradeComponent.setQuoteQuantity(model.defaultQuoteQuantity);
                    tradeComponent.setQuoteType(model.defaultQuoteType);
                    tradeOrderTypeComponents.forEach((x) => {
                        x.syncValue();
                    });
                }

                this.close();
            });
        });
        this.popup?.querySelectorAll<HTMLElement>('.close').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                this.close();
            });
        });
        this.popup?.querySelectorAll<HTMLElement>('.section').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                el.classList.toggle('active');
            });
        });
    }

    private show() {
        const model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
        if (model) {
            const checkboxDontShowAgain = this.popup?.querySelector<HTMLInputElement>('#chart-settings-dont-show-again');
            if (checkboxDontShowAgain) {
                checkboxDontShowAgain.checked = true;
                if (!model.displayOrderConfirmationPopup) {
                    checkboxDontShowAgain.checked = false;
                }
            }
            
            if (this.base.tiChartSeries.isPreMarketEnabled()) {
                const checkboxShowPremarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-per-market');
                if (checkboxShowPremarket) {
                    checkboxShowPremarket.checked = model.displayPremarket;
                }
            }
            
            if (this.base.tiChartSeries.isPostMarketEnabled()) {
                const checkboxShowPostmarket = this.popup?.querySelector<HTMLInputElement>('#chart-settings-show-post-market');
                if (checkboxShowPostmarket) {
                    checkboxShowPostmarket.checked = model.displayPostmarket;
                }
            }

            const checkboxDisplayChartSettingZoomAndScroll = this.popup?.querySelector<HTMLInputElement>('#chart-settings-zoom-and-scroll');
            if (checkboxDisplayChartSettingZoomAndScroll) {
                checkboxDisplayChartSettingZoomAndScroll.checked = false;
                if (model.displayChartSettingZoomAndScroll) {
                    checkboxDisplayChartSettingZoomAndScroll.checked = true;
                }
            }

            const checkboxDisplayPnPBrowserChartsByDefault = this.popup?.querySelector<HTMLInputElement>('#chart-settings-pnp-browser-charts-by-default');
            if (checkboxDisplayPnPBrowserChartsByDefault) {
                checkboxDisplayPnPBrowserChartsByDefault.checked = false;
                if (model.pnpBrowserChartsByDefault) {
                    checkboxDisplayPnPBrowserChartsByDefault.checked = true;
                }
            }

            const checkboxDisplayRightEdgePadding = this.popup?.querySelector<HTMLInputElement>('#chart-settings-enable-right-edge-padding');
            if (checkboxDisplayRightEdgePadding) {
                checkboxDisplayRightEdgePadding.checked = false;
                if (model.enableRightEdgePadding) {
                    checkboxDisplayRightEdgePadding.checked = true;
                }
            }

            const checkboxDisplayMainChartChangePercent = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-main-chart-change-percent');
            if (checkboxDisplayMainChartChangePercent) {
                checkboxDisplayMainChartChangePercent.checked = false;
                if (model.displayMainChartChangePercentVisibleCandles) {
                    checkboxDisplayMainChartChangePercent.checked = true;
                }
            }

            const checkboxDisplayChartSettingBuySellFlags = this.popup?.querySelector<HTMLInputElement>('#chart-settings-display-buy-sell-flags');
            if (checkboxDisplayChartSettingBuySellFlags) {
                checkboxDisplayChartSettingBuySellFlags.checked = false;
                if (model.displayChartSettingBuySellFlags) {
                    checkboxDisplayChartSettingBuySellFlags.checked = true;
                }
            }

            if (this.quoteQuantityInput) {
                if (model.defaultQuoteQuantity > 0) {
                    this.quoteQuantityInput.set(model.defaultQuoteQuantity)
                }
            }

            const defaultTradeSizeOptions = this.popup?.querySelectorAll<HTMLElement>('.default-trade-size li');
            const defaultTradeSizeLabel = this.popup?.querySelector<HTMLElement>('.default-trade-size .selected p');
            if (defaultTradeSizeOptions?.length && defaultTradeSizeLabel) {
                let labelValue = '';
                defaultTradeSizeOptions.forEach((opt: HTMLElement) => {
                    if (model.defaultQuoteType == opt.getAttribute('data-value')) {
                        opt.classList.add('selected');
                        labelValue = opt.innerHTML;
                    } else {
                        opt.classList.remove('selected');
                    }
                });


                if (labelValue) {
                    defaultTradeSizeLabel.innerHTML = labelValue;
                }
            }
        }

        this.popup?.addClass('active');
    }

    private close() {
        this.popup?.removeClass('active');
    }

    private closePicker() {
        const tradeTypePickerElement = this.popup?.querySelector('.font-size-picker-wrapper');
        if (tradeTypePickerElement) {
            tradeTypePickerElement.classList.remove('active');
        }
    }

    private onPickerClick() {
        const pickerElement = this.popup?.querySelector('.font-size-picker-wrapper');
        if (pickerElement) {
            pickerElement.addEventListener('click', () => {
                pickerElement.classList.toggle('active');
            });

            const options =  pickerElement.querySelectorAll<HTMLElement>('li');

           options.forEach((li: HTMLElement) => {
                li.addEventListener('click', () => {
                    options.forEach((opt: HTMLElement) => {
                        opt.classList.remove('selected');
                    });

                    li.classList.add('selected');
                    const label = pickerElement.querySelector('.selected p');
                    if (label) {
                        label.innerHTML = li.innerHTML;
                    }
                });
            });
        }
    }

    private create() {
        this.element.classList.remove('hidden');
        const div = document.createElement('div');
        div.className = `chart-settings settings-popup ${this.base.marketingWebChartMode ? '' : 'tipro'}`;
        if (this.base.marketingWebChartMode) {
            div.innerHTML = `
            <div class="wrapper">
                <div class="head">
                    <p class="title">Chart Settings</p>
                    <button class="close" type="button">
                        <span class="icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" width="17" height="17" fill="currentColor">
                                <path d="m.58 1.42.82-.82 15 15-.82.82z"></path>
                                <path d="m.58 15.58 15-15 .82.82-15 15z"></path>
                            </svg>
                        </span>
                    </button>
                </div>
                <div class="content" style="overflow: visible; max-height: ${this.base.chartWrapper?.clientHeight - 150}px;">
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-dont-show-again" />
                        <label for="chart-settings-dont-show-again">Display Order Confirmation</label>
                    </div>
                    <div class="checkbox-input ${this.base.tiChartSeries.isPreMarketDisabled() ? 'hidden' : ''}">
                        <input type="checkbox" class="mgr-8" id="chart-settings-show-per-market" />
                        <label for="chart-settings-show-per-market">Display Premarket</label>
                    </div>
                    <div class="checkbox-input  ${this.base.tiChartSeries.isPostMarketDisabled() ? 'hidden' : ''}">
                        <input type="checkbox" class="mgr-8" id="chart-settings-show-post-market" />
                        <label for="chart-settings-show-post-market">Display Postmarket</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-zoom-and-scroll" />
                        <label for="chart-settings-zoom-and-scroll">Display Zoom and Scroll Controls</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-display-main-chart-change-percent" />
                        <label for="chart-settings-display-main-chart-change-percent">Display main chart Change Percent</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-display-buy-sell-flags" />
                        <label for="chart-settings-display-buy-sell-flags">Display Buy Sell Flags</label>
                    </div>
                    <hr/>
                    <div class="control default-trade-size">
                        <span class="label-inline" style="width: 120px; margin-right: 4px;">Default Trade Size</span>
                        <div class="font-size-picker-wrapper mgr-4">
                            <div class="font-size-picker">
                                <div class="selected">
                                    <p>Shares</p>
                                </div>
                                <div class="arrow">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11 7" width="11" height="7"
                                        fill="none">
                                        <path stroke="currentColor" stroke-width="1.3" d="M.5 1.5l5 4 5-4"></path>
                                    </svg>
                                </div>
                                <ul class="selection">
                                    <li data-value="Shares" class="selected">Shares</li>
                                    <li data-value="Dollars">Dollars</li>
                                </ul>
                            </div>
                        </div>
                        <div class="text-input">
                            <input type="text" inputmode="decimal" pattern="[0-9]*" value="" />
                        </div>
                    </div>
                </div>
                <div class="footer">
                    <button class="close">Close</button>
                    <button class="submit">Ok</button>
                </div>
            </div>`;
        } else {
            div.innerHTML = `
            <div class="wrapper">
                <div class="head">   
                    <p class="title">Chart Settings</p>
                    <button class="close" type="button">
                        <span class="icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" width="17" height="17" fill="currentColor">
                                <path d="m.58 1.42.82-.82 15 15-.82.82z"></path>
                                <path d="m.58 15.58 15-15 .82.82-15 15z"></path>
                            </svg>
                        </span>
                    </button>
                </div>
                <div class="content" style="overflow: visible; max-height: ${this.base.chartWrapper?.clientHeight - 150}px;">
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-zoom-and-scroll" />
                        <label for="chart-settings-zoom-and-scroll">Display Zoom and Scroll Controls</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-display-main-chart-change-percent" />
                        <label for="chart-settings-display-main-chart-change-percent">Display main chart Change Percent</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-display-buy-sell-flags" />
                        <label for="chart-settings-display-buy-sell-flags">Display Buy Sell Flags</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-enable-right-edge-padding" />
                        <label for="chart-settings-enable-right-edge-padding">Enable Right-Edge Padding</label>
                    </div>
                    <div class="checkbox-input">
                        <input type="checkbox" class="mgr-8" id="chart-settings-pnp-browser-charts-by-default" />
                        <label for="chart-settings-pnp-browser-charts-by-default">Use Browser Charts by Default</label>
                    </div>
                </div>
                <div class="footer">
                    <button class="close">Close</button>
                    <button class="submit">Ok</button>
                </div>
            </div>`;
        }

        this.popup = div;
        this.base.chartWrapper?.insertAdjacentElement('beforeend', this.popup);
        setTimeout(() => {
            this.onViewAllChartOptionsClick();
            this.onPickerClick();
            this.initAutoNumberic();
        }, 150);
    }

    private onViewAllChartOptionsClick() {
        const viewAllChartOptionsElement = this.popup?.querySelector<HTMLElement>('.view-all-chart-options');
        if (viewAllChartOptionsElement) {
            viewAllChartOptionsElement.addEventListener('click', () => {
                this.base.chartService.requestOpenAllChartOptions();
            });
        }
    }

    private initAutoNumberic() {
        const quoteQuantityInputElement = this.popup?.querySelector<HTMLElement>('.default-trade-size input');

        if (quoteQuantityInputElement) {
            const model = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
            this.quoteQuantityInput = new AutoNumeric(quoteQuantityInputElement, model?.defaultQuoteQuantity ?? 1000, {
                decimalPlaces: 0
            });
        }
    }

    private onBodyClick() {
        this.base.domEvents.onBodyClick(this.id, (ev: any) => {
            const target = ev.target as HTMLElement;
            if (target.classList.contains('font-size-picker-wrapper') || !!target.closest('.font-size-picker-wrapper')) {
                return;
            }

            this.closePicker();
        });
    }
}